import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Icon from '@dustin-web/microsite-components/src/icon'
import classNames from 'classnames'
import {
  useResponsive,
  useSiteContext,
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const Spinner = dynamic(() => import('@dustin-web/microsite-components/src/spinner'))

declare global {
  interface Window {
    zE: (action: string, ...args: unknown[]) => void
  }
}

declare const zE: (action: string, ...args: unknown[]) => void

const ZD_SESSION_KEY = 'ZD-buid'

const envList = ['test', 'local', 'staging']

const chatKeySet: Record<string, string> = {
  SE: '3971b4ad-0a44-4027-b0b4-e2fc1ecc8bad',
  DK: '14aaca48-da82-4f4f-b8ea-a66c7d6b2945',
  NO: 'f9846ef2-78c6-494d-89bc-b9000dda71ed',
  FI: '05d28232-10a7-4af4-b971-6e9f8626d1f2',
  NL: 'c17ed772-8754-48d6-b9b7-ea282f6e0775',
  SANDBOX: 'be70ff64-6d4f-4003-a9bc-8e6dc0ff8ae8',
} as const

const excludedStaticPages = ['/shoppingcart', '/checkoutflow', '/checkoutsite', '/brands']
const excludedPages = ['/']
const excludeFilterPage = 'group'

const RenderZendeskChat = ({ path }: { path: string }) => {
  const { isSmallScreen } = useResponsive()
  const { twoLetterISOLanguageName } = useUserSettings()
  const { sourcingCompany, serverEnv } = useSiteContext()
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [loadingScript, setLoadingScript] = useState(false)

  const unreadMessages = useRef<HTMLSpanElement | null>(null)

  const { translate } = useTranslations()
  const { getZendeskToken, isAuthenticated, userData } = useAuth()
  const { enabled: zendeskAuthToggle } = useFeatureFlag('enable-zendesk-auth')
  const customerErp = userData?.contact?.customerErp
  const customerId = userData?.contact?.customerId
  const contactId = userData?.contact?.id

  useEffect(() => {
    const resumeChat = localStorage.getItem(ZD_SESSION_KEY)

    if (!!resumeChat && !scriptLoaded) {
      loadZendesk(false)
    }

    window.addEventListener('openZendeskChat', openChat)

    return () => {
      window.removeEventListener('openZendeskChat', openChat)
    }
  })

  const lang = twoLetterISOLanguageName === 'nb' ? 'no' : twoLetterISOLanguageName
  const chatKey = chatKeySet[envList.includes(serverEnv) ? 'SANDBOX' : sourcingCompany]
  const url = typeof document !== 'undefined' && document.URL
  const params = path.split('/')

  const disableFilterPage = params[1] === excludeFilterPage && params.length > 4
  const isPageDisabled = excludedPages.includes(path)
  const isStaticPage = excludedStaticPages.some(str => path.includes(str))
  const hideChatButton = isStaticPage || isPageDisabled || disableFilterPage

  const initializeZendesk = (openChat: boolean) => {
    // Initialize Zendesk settings after the script is loaded
    // allows support to pickup selected lang in browser, because english is not supported yet.
    zE('messenger:set', 'conversationFields', [
      { id: '25235101622929', value: url },
      { id: '28467280679953', value: lang },
      { id: '39341791419793', value: customerErp },
      { id: '43051645717009', value: contactId },
      { id: '43051686027921', value: customerId },
    ])

    zE('messenger:set', 'locale', lang)
    zE('messenger', 'close')

    if (openChat) {
      zE('messenger', 'open')
    }

    setTimeout(() => {
      setLoadingScript(false)
    }, 500)

    const conversationBadge = document.querySelector('#conversation-badge') as HTMLElement

    const populateUnreadIndicator = (count: number) => {
      if (count > 0) {
        const { current } = unreadMessages || {}

        if (!current) {
          return
        }

        current.style.background = '#CC3333'
        current.innerHTML = count.toString()
        current.classList.add('tilt-animation')
      } else {
        resetUnreadIndicator()
      }
    }

    const resetUnreadIndicator = () => {
      const { current } = unreadMessages || {}

      if (!current) {
        return
      }

      current.style.background = 'gray'
      current.innerHTML = '0'
      conversationBadge.classList.remove('tilt-animation')
    }

    setTimeout(() => {
      zE('messenger:on', 'unreadMessages', (count: number) => {
        populateUnreadIndicator(count)
      })
    }, 1000)

    resetUnreadIndicator()
  }

  const loadZendesk = (openChat = true) => {
    if (!chatKey) {
      console.error(`Error: zendesk chatkey is undefined for ${lang} market`)
      return
    }

    const script = document.createElement('script')

    script.id = 'ze-snippet'
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${chatKey}`
    script.onload = () => initializeZendesk(openChat)

    document.body.appendChild(script)

    setScriptLoaded(true)
    setLoadingScript(true)
  }

  const openChat: any = async () => {
    if (!scriptLoaded) {
      loadZendesk()
      return
    }
    try {
      if (zendeskAuthToggle) {
        if (isAuthenticated) {
          const token = await getZendeskToken()
          if (token) {
            zE('messenger', 'loginUser', (callback: any) => callback(token))
          }
        } else {
          zE('messenger', 'logoutUser')
        }
      }
    } catch (error) {
      console.log(error)
    }
    window.zE('messenger', 'open')
  }

  return (
    <div
      className={classNames('zendesk-chat', {
        'd-none': hideChatButton,
        mobile: isSmallScreen,
      })}>
      <button
        id="conversation-badge"
        aria-label={translate('Aria_Label_Open_Zendesk_Support')}
        onClick={openChat}>
        {!loadingScript ? (
          <div className="d-flex flex-column">
            <Icon name="speech-bubble-2" className="i-24" />
            <span style={{ fontSize: '10px', fontWeight: 700 }}>
              {translate('Chat_Button_Text')}
            </span>
          </div>
        ) : (
          <Spinner local size="xxs" />
        )}
      </button>
      {scriptLoaded && !loadingScript && (
        <span ref={unreadMessages} id="unread-indicator">
          0
        </span>
      )}
    </div>
  )
}

export default RenderZendeskChat
